'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useState } from 'react'
import { ICityBrand } from '@/api/brands/get-brand-query'
import { Locale } from '@/setup/locales'
import { getLinkHref } from '@/setup/routing'

interface ICityBrandsListProps {
    citySlug: string
    brands: ICityBrand[]
}

export const CityBrandsList = ({ citySlug, brands }: ICityBrandsListProps) => {
    const locale = useLocale() as Locale
    const t = useTranslations()

    // Some brands carry a logo url that 404s. Without this the row renders the browser's
    // broken-image glyph, which looks worse than no logo at all.
    const [brokenLogos, setBrokenLogos] = useState<Record<string, true>>({})

    return (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {brands.map((brand) => (
                <li key={brand.slug}>
                    <Link
                        href={getLinkHref(
                            {
                                pattern: '/restaurants/[citySlug]/brands/[brandSlug]',
                                params: { citySlug, brandSlug: brand.slug },
                            },
                            locale
                        )}
                        className="flex flex-row items-center gap-4 rounded-l border-[1px]
                            border-gray-200 p-4 hover:bg-gray-50 transition-colors h-full"
                    >
                        {brand.logoUrl && !brokenLogos[brand.slug] && (
                            <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-s">
                                <Image
                                    src={brand.logoUrl}
                                    alt=""
                                    fill
                                    sizes="48px"
                                    className="object-contain"
                                    onError={() =>
                                        setBrokenLogos((broken) => ({
                                            ...broken,
                                            [brand.slug]: true,
                                        }))
                                    }
                                />
                            </div>
                        )}

                        <div className="flex flex-col">
                            <span className="text-[16px] md:text-[17px] font-semibold">
                                {brand.name}
                            </span>

                            <span className="text-[13px] md:text-[14px] text-black/60">
                                {t('restaurants.brands.locationCount', {
                                    count: brand.locationCount,
                                })}
                            </span>
                        </div>
                    </Link>
                </li>
            ))}
        </ul>
    )
}
